/**
 * RgbObjectVariant - Variante RGB Object para Color
 *
 * Representa cor como objeto { r, g, b, a? }
 *
 * @example
 * { r: 255, g: 0, b: 0 }
 * { r: 255, g: 0, b: 0, a: 0.5 }
 */

import { BaseVariant } from "../../../contracts";
import type { ValidationResult, FormatOptions } from "../../../types";
import type { RGBA, ColorRgbObject, ColorFormatOptions } from "../types";
import {
  NAMED_COLORS,
  normalizeRgbChannel,
  normalizeAlpha,
  hslToRgb,
} from "../types";

export class RgbObjectVariant extends BaseVariant<ColorRgbObject, RGBA> {
  readonly name = "rgb_object";
  readonly type = "object" as const;

  // ===========================================================================
  // CONVERT
  // ===========================================================================

  toBase(value: ColorRgbObject): RGBA {
    return {
      r: normalizeRgbChannel(value.r),
      g: normalizeRgbChannel(value.g),
      b: normalizeRgbChannel(value.b),
      a: normalizeAlpha(value.a),
    };
  }

  fromBase(base: RGBA): ColorRgbObject {
    const result: ColorRgbObject = {
      r: normalizeRgbChannel(base.r),
      g: normalizeRgbChannel(base.g),
      b: normalizeRgbChannel(base.b),
    };

    // Only include alpha if not 1
    if (base.a !== undefined && base.a < 1) {
      result.a = normalizeAlpha(base.a);
    }

    return result;
  }

  // ===========================================================================
  // CAST
  // ===========================================================================

  cast(input: unknown): ColorRgbObject | null {
    // Already an RGB object
    if (
      typeof input === "object" &&
      input !== null &&
      "r" in input &&
      "g" in input &&
      "b" in input
    ) {
      const obj = input as { r: unknown; g: unknown; b: unknown; a?: unknown };

      if (
        typeof obj.r !== "number" ||
        typeof obj.g !== "number" ||
        typeof obj.b !== "number"
      ) {
        return null;
      }

      return this.fromBase({
        r: obj.r,
        g: obj.g,
        b: obj.b,
        a: typeof obj.a === "number" ? obj.a : 1,
      });
    }

    // HSL object
    if (
      typeof input === "object" &&
      input !== null &&
      "h" in input &&
      "s" in input &&
      "l" in input
    ) {
      const obj = input as { h: number; s: number; l: number; a?: number };
      const rgb = hslToRgb(obj.h, obj.s, obj.l);
      return this.fromBase({
        ...rgb,
        a: obj.a ?? 1,
      });
    }

    // String formats
    if (typeof input === "string") {
      const trimmed = input.trim().toLowerCase();

      // Named color
      if (NAMED_COLORS[trimmed]) {
        return this.fromBase(NAMED_COLORS[trimmed]);
      }

      // Hex format
      const hexMatch = trimmed.match(
        /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i,
      );
      if (hexMatch) {
        return this.fromBase({
          r: parseInt(hexMatch[1], 16),
          g: parseInt(hexMatch[2], 16),
          b: parseInt(hexMatch[3], 16),
          a: hexMatch[4] ? parseInt(hexMatch[4], 16) / 255 : 1,
        });
      }

      // Shorthand hex
      const hexShortMatch = trimmed.match(
        /^#?([0-9a-f])([0-9a-f])([0-9a-f])([0-9a-f])?$/i,
      );
      if (hexShortMatch) {
        return this.fromBase({
          r: parseInt(hexShortMatch[1] + hexShortMatch[1], 16),
          g: parseInt(hexShortMatch[2] + hexShortMatch[2], 16),
          b: parseInt(hexShortMatch[3] + hexShortMatch[3], 16),
          a: hexShortMatch[4]
            ? parseInt(hexShortMatch[4] + hexShortMatch[4], 16) / 255
            : 1,
        });
      }

      // rgb()/rgba() string
      const rgbMatch = trimmed.match(
        /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (rgbMatch) {
        return this.fromBase({
          r: parseInt(rgbMatch[1], 10),
          g: parseInt(rgbMatch[2], 10),
          b: parseInt(rgbMatch[3], 10),
          a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
        });
      }

      // hsl()/hsla() string
      const hslMatch = trimmed.match(
        /^hsla?\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?\s*(?:,\s*([\d.]+))?\s*\)$/,
      );
      if (hslMatch) {
        const rgb = hslToRgb(
          parseInt(hslMatch[1], 10),
          parseInt(hslMatch[2], 10),
          parseInt(hslMatch[3], 10),
        );
        return this.fromBase({
          ...rgb,
          a: hslMatch[4] ? parseFloat(hslMatch[4]) : 1,
        });
      }
    }

    return null;
  }

  // ===========================================================================
  // VALIDATE
  // ===========================================================================

  validate(value: ColorRgbObject): ValidationResult {
    const errors: string[] = [];

    if (typeof value !== "object" || value === null) {
      errors.push("RGB color must be an object");
      return { valid: false, errors };
    }

    if (!("r" in value) || !("g" in value) || !("b" in value)) {
      errors.push("RGB color must have r, g, b properties");
      return { valid: false, errors };
    }

    if (
      typeof value.r !== "number" ||
      typeof value.g !== "number" ||
      typeof value.b !== "number"
    ) {
      errors.push("r, g, b must be numbers");
      return { valid: false, errors };
    }

    if (value.r < 0 || value.r > 255) {
      errors.push("r must be between 0 and 255");
    }
    if (value.g < 0 || value.g > 255) {
      errors.push("g must be between 0 and 255");
    }
    if (value.b < 0 || value.b > 255) {
      errors.push("b must be between 0 and 255");
    }

    if (value.a !== undefined) {
      if (typeof value.a !== "number") {
        errors.push("a must be a number");
      } else if (value.a < 0 || value.a > 1) {
        errors.push("a must be between 0 and 1");
      }
    }

    return { valid: errors.length === 0, errors };
  }

  // ===========================================================================
  // FORMAT
  // ===========================================================================

  format(value: ColorRgbObject, options?: FormatOptions): string {
    const opts = options as ColorFormatOptions | undefined;
    const rgba = this.toBase(value);

    const r = rgba.r;
    const g = rgba.g;
    const b = rgba.b;
    const a = rgba.a;

    if (opts?.includeAlpha || a < 1) {
      if (opts?.compact) {
        return `rgba(${r},${g},${b},${a})`;
      }
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    if (opts?.compact) {
      return `rgb(${r},${g},${b})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
  }
}
